import React, { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import "./design_requests.css";

const FILTERS = [
    { key: "all", label: "All" },
    { key: "new", label: "New" },
    { key: "seen", label: "Seen" },
    { key: "replied", label: "Replied" },
];

const toDate = (timestamp) => {
    if (!timestamp) return null;
    try {
        return timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    } catch {
        return null;
    }
};

const formatWhen = (timestamp) => {
    const date = toDate(timestamp);
    if (!date || Number.isNaN(date.getTime())) return "";
    return date.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
};

const phoneDigits = (phone) => String(phone || "").replace(/\D/g, "");

const waNumber = (phone) => {
    const digits = phoneDigits(phone);
    if (digits.length === 10) return `91${digits}`;
    return digits;
};

const statusOf = (item) => (item.status === "seen" || item.status === "replied" ? item.status : "new");

function DesignRequestsPage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [filter, setFilter] = useState("all");
    const [search, setSearch] = useState("");
    const [photo, setPhoto] = useState(null);

    useEffect(() => {
        const unsubscribe = db.collection("queries")
            .where("formType", "==", "design_photo")
            .onSnapshot((snapshot) => {
                const rows = snapshot.docs.map((doc) => ({ docId: doc.id, ...doc.data() }));
                rows.sort((a, b) => {
                    const da = toDate(a.createdAt)?.getTime() || 0;
                    const db_ = toDate(b.createdAt)?.getTime() || 0;
                    return db_ - da;
                });
                setItems(rows);
                setLoading(false);
                setError("");
            }, (err) => {
                console.error("design requests", err);
                setLoading(false);
                setError("Design requests could not be loaded.");
            });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        if (!photo) return undefined;
        const onKey = (e) => { if (e.key === "Escape") setPhoto(null); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [photo]);

    const counts = useMemo(() => {
        const next = { all: items.length, new: 0, seen: 0, replied: 0 };
        items.forEach((item) => { next[statusOf(item)] += 1; });
        return next;
    }, [items]);

    const visible = useMemo(() => {
        const q = search.trim().toLowerCase();
        return items.filter((item) => {
            if (filter !== "all" && statusOf(item) !== filter) return false;
            if (!q) return true;
            const hay = [
                item.name, item.phone, item.email, item.city,
                item.garment, item.occasion, item.requestType, item.des, item.fabricNote,
            ].join(" ").toLowerCase();
            return hay.includes(q);
        });
    }, [items, filter, search]);

    const setStatus = (docId, status) => {
        db.collection("queries").doc(docId).update({
            status,
            statusAt: new Date(),
        }).catch(() => {});
    };

    return (
        <div className="dreq">
            <div className="dreq-stats">
                <div><strong>{counts.all}</strong><span>Total</span></div>
                <div><strong>{counts.new}</strong><span>New</span></div>
                <div><strong>{counts.seen}</strong><span>Seen</span></div>
                <div><strong>{counts.replied}</strong><span>Replied</span></div>
            </div>

            <div className="dreq-tools">
                <div className="dreq-filters">
                    {FILTERS.map((item) => (
                        <button
                            key={item.key}
                            type="button"
                            className={filter === item.key ? "is-on" : ""}
                            onClick={() => setFilter(item.key)}
                        >
                            {item.label}
                            <em>{counts[item.key]}</em>
                        </button>
                    ))}
                </div>
                <input
                    type="search"
                    value={search}
                    placeholder="Name, phone, city, garment"
                    onChange={(e) => setSearch(e.target.value)}
                />
            </div>

            {loading && <p className="dreq-note">Loading design requests…</p>}
            {error && <p className="dreq-note is-bad">{error}</p>}
            {!loading && !error && visible.length === 0 && (
                <p className="dreq-note">No design photo requests in this view.</p>
            )}

            <div className="dreq-list">
                {visible.map((item) => {
                    const status = statusOf(item);
                    const photos = Array.isArray(item.photos) ? item.photos.filter(Boolean) : [];
                    const wa = waNumber(item.phone);
                    return (
                        <article key={item.docId} className={`dreq-card is-${status}`}>
                            <header>
                                <div>
                                    <strong>{item.name || "Customer"}</strong>
                                    <span>{formatWhen(item.createdAt)}</span>
                                </div>
                                <em className={`dreq-status is-${status}`}>
                                    {status === "new" ? "New" : status === "seen" ? "Seen" : "Replied"}
                                </em>
                            </header>

                            <div className="dreq-chips">
                                {item.requestType && <span>{item.requestType}</span>}
                                {item.garment && <span>{item.garment}</span>}
                                {item.occasion && <span>{item.occasion}</span>}
                                {item.quantity && <span>Qty {item.quantity}</span>}
                                {item.city && <span>{item.city}</span>}
                            </div>

                            <div className="dreq-contact">
                                {item.phone && <span>{item.phone}</span>}
                                {item.email && <span>{item.email}</span>}
                            </div>

                            {item.fabricNote && <p><b>Fabric: </b>{item.fabricNote}</p>}
                            {item.des && <p>{item.des}</p>}
                            {item.photosFailed > 0 && (
                                <p className="dreq-warn">
                                    {item.photosFailed} photo{item.photosFailed > 1 ? "s" : ""} did not upload. Ask them on WhatsApp.
                                </p>
                            )}

                            {photos.length > 0 && (
                                <div className="dreq-photos">
                                    {photos.map((url, index) => (
                                        <button key={url} type="button" onClick={() => setPhoto({ url, name: item.name, index, total: photos.length })}>
                                            <img src={url} alt="" />
                                        </button>
                                    ))}
                                </div>
                            )}

                            <footer>
                                {wa && <a href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer">WhatsApp</a>}
                                {wa && <a href={`tel:+${wa}`}>Call</a>}
                                {item.email && <a href={`mailto:${item.email}`}>Email</a>}
                                {status !== "seen" && <button type="button" onClick={() => setStatus(item.docId, "seen")}>Mark seen</button>}
                                {status !== "replied" && <button type="button" className="is-primary" onClick={() => setStatus(item.docId, "replied")}>Mark replied</button>}
                                {status === "replied" && <button type="button" onClick={() => setStatus(item.docId, "new")}>Mark new again</button>}
                            </footer>
                        </article>
                    );
                })}
            </div>

            {photo && (
                <div className="dreq-pop" onClick={() => setPhoto(null)}>
                    <div className="dreq-pop-box" onClick={(e) => e.stopPropagation()}>
                        <header>
                            <span>{photo.name || "Design photo"} · {photo.index + 1}/{photo.total}</span>
                            <button type="button" onClick={() => setPhoto(null)}>Close</button>
                        </header>
                        <img src={photo.url} alt="" />
                    </div>
                </div>
            )}
        </div>
    );
}

export default DesignRequestsPage;
